import * as yup from 'yup';

const dayOptions = Array.from({ length: 28 }, (_, i) => (i + 1).toString()).concat('末日');
const monthOptions = ['当月', '翌月', '翌々月', '3か月後', '4か月後', '5か月後', '6か月後'];

const nullableNumber = (message: string) =>
  yup
    .number()
    .typeError(message)
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === undefined || originalValue === null) return null;
      return String(originalValue).trim() === '' ? null : Number(value);
    });

export const schema = yup.object({
  contractor_name: yup.string().required('契約先名は必須です'),
  applicable_contractor_names: yup.string().nullable().max(65_535, '適用対象契約先名は65535文字以内で入力してください'),
  contract_type: yup.string().oneOf(['billing', 'payment'], '契約区分を選択してください').required('契約区分は必須です'),
  start_date: yup.string().required('契約開始日は必須です'),
  end_date: yup.string().nullable(),
  status: yup.string().oneOf(['active', 'inactive'], 'ステータスを選択してください').required('ステータスは必須です'),
  closing_day: yup.string().oneOf(dayOptions, '締め日を選択してください').required('締め日は必須です'),
  payment_month: yup.string().oneOf(monthOptions, '入金/支払月を選択してください').required('入金/支払月は必須です'),
  payment_day: yup.string().oneOf(dayOptions, '入金/支払日を選択してください').required('入金/支払日は必須です'),
  pricing_settings: yup
    .array()
    .of(
      yup.object({
        pricing_method: yup.string().oneOf(['個建て', '車建て', 'トンキロ建て'], '課金方式を選択してください').required('課金方式は必須です'),
        base_amount: yup
          .number()
          .when('pricing_method', {
            is: 'トンキロ建て',
            then: (schema) => schema.nullable().transform((value, originalValue) => {
              if (originalValue === undefined || originalValue === null) return null;
              return String(originalValue).trim() === '' ? null : Number(value);
            }),
            otherwise: (schema) =>
              schema
                .required('基本料金は必須です')
                .positive('基本料金は0より大きい値を入力してください')
                .integer('整数で入力してください')
                .typeError('数値を入力してください'),
          }),
        excess_rate: nullableNumber('超過料金には数値を入力してください').when('pricing_method', {
          is: '個建て',
          then: (schema) => schema.min(0, '0以上の値を入力してください'),
          otherwise: (schema) => schema.nullable(),
        }),
        excess_threshold: nullableNumber('超過閾値には数値を入力してください').when('pricing_method', {
          is: '個建て',
          then: (schema) => schema.min(0, '0以上の値を入力してください'),
          otherwise: (schema) => schema.nullable(),
        }),
        minimum_order: nullableNumber('最低発注量には数値を入力してください').when('pricing_method', {
          is: '個建て',
          then: (schema) => schema.min(0, '0以上の値を入力してください'),
          otherwise: (schema) => schema.nullable(),
        }),
        minimum_order_fee: nullableNumber('最低発注量割れ時の料金には数値を入力してください').when('pricing_method', {
          is: '個建て',
          then: (schema) => schema.min(0, '0以上の値を入力してください'),
          otherwise: (schema) => schema.nullable(),
        }),
        distance_weight_settings: yup
          .array()
          .of(
            yup.object({
              id: yup
                .number()
                .required('距離×重量別料金表のIDが不正です')
                .integer('距離×重量別料金表のIDが不正です')
                .typeError('距離×重量別料金表のIDが不正です'),
              distance_min: nullableNumber('距離(最小)には数値を入力してください').min(0, '0以上の値を入力してください'),
              distance_max: nullableNumber('距離(最大)には数値を入力してください')
                .min(0, '0以上の値を入力してください')
                .test(
                  'distance-min-lte-max',
                  '距離(最大)は距離(最小)以上で入力してください',
                  function (value) {
                    const parent = this.parent as { distance_min?: number | null } | undefined;
                    const min = parent?.distance_min;
                    if (min == null || value == null) return true;
                    return min <= value;
                  }
                ),
              weight_min: nullableNumber('重量(最小)には数値を入力してください').min(0, '0以上の値を入力してください'),
              weight_max: nullableNumber('重量(最大)には数値を入力してください')
                .min(0, '0以上の値を入力してください')
                .test(
                  'weight-min-lte-max',
                  '重量(最大)は重量(最小)以上で入力してください',
                  function (value) {
                    const parent = this.parent as { weight_min?: number | null } | undefined;
                    const min = parent?.weight_min;
                    if (min == null || value == null) return true;
                    return min <= value;
                  }
                ),
              price: yup
                .number()
                .required('料金は必須です')
                .min(0, '0以上の値を入力してください')
                .integer('整数で入力してください')
                .typeError('料金には数値を入力してください'),
            })
          )
          .nullable()
          .when('pricing_method', {
            is: 'トンキロ建て',
            then: (schema) => schema.min(1, '少なくとも1つの距離×重量別料金設定が必要です').required(),
            otherwise: (schema) => schema.nullable(),
          }),
        area_settings: yup
          .array()
          .of(
            yup.object({
              area_type: yup.string().required('エリア種別を選択してください'),
              loading_area: yup.string().required('積地を入力してください'),
              unloading_area: yup.string().required('降地を入力してください'),
            })
          )
          .nullable(),
        temperature_types: yup.array().of(yup.string()).nullable(),
        vehicle_types: yup.array().of(yup.string()).nullable().when('pricing_method', {
          is: '車建て',
          then: (schema) => schema.min(1, '対象車種を1つ以上選択してください').required(),
          otherwise: (schema) => schema.nullable(),
        }),
        conditions: yup.string().nullable(),
      })
    )
    .min(1, '少なくとも1つの料金設定が必要です')
    .required('料金設定は必須です'),
  conditions: yup.string().nullable(),
  company_id: yup.number().required('事業所は必須です'),
  // 新しいdocumentsフィールドのバリデーション
  documents: yup
    .array()
    .of(
      yup.object({
        type: yup.string().oneOf(['existing', 'new']).required(),
        id: yup.string().required(),
        filename: yup.string().required(),
        url: yup.string().when('type', {
          is: 'existing',
          then: (schema) => schema.required('既存ファイルにはURLが必要です'),
          otherwise: (schema) => schema.nullable(),
        }),
        file: yup.mixed().when('type', {
          is: 'new',
          then: (schema) => schema.required('新規ファイルにはFileオブジェクトが必要です'),
          otherwise: (schema) => schema.nullable(),
        }),
      })
    )
    .max(10, '契約書は最大10ファイルまでです')
    .nullable(),
  deleted_document_urls: yup.array().of(yup.string()).nullable(),
  // 互換性のため残す
  document_urls: yup.array().of(yup.string()).nullable(),
  document_signed_ids: yup.array().of(yup.string()).nullable(),
  has_document: yup.boolean().nullable(),
});
